import { useState, useEffect } from 'react';
import { useNavigate, useParams, useOutletContext } from 'react-router-dom';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { onboardingApi } from '../services/onboardingApi';
import { useImportedAccounts, useAccountConfigurations } from '../hooks/useAccountConfiguration';
import {
  OnboardingProject,
  BillSource,
  BillSourceType,
  CreateBillSourceRequest,
  GLCodeMapping,
  CreateGLCodeMappingRequest,
  Tag,
  TagAssignment,
  EntityType,
} from '@/types/onboarding';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertCircle,
  CheckCircle2,
  Plus,
  Edit,
  Trash2,
  Mail,
  Globe,
  FileText,
  Tag as TagIcon,
  Settings,
  Zap,
  Loader2,
  Download,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface OutletContext {
  project: OnboardingProject | null;
  setProject: (project: OnboardingProject | null) => void;
  loadProject: () => Promise<void>;
}

const Stage4Mapping = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { currentWorkspace } = useWorkspace();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { project, setProject } = useOutletContext<OutletContext>();

  // Load real accounts from database
  const { data: importedAccounts = [], isLoading: loadingAccounts } = useImportedAccounts(projectId);
  const { data: configurations = [] } = useAccountConfigurations(projectId);

  // Transform imported accounts with config status
  const accounts = importedAccounts.map(account => {
    const config = configurations.find(c => c.imported_account_id === account.id);
    return {
      id: account.id,
      account_number: account.account_number || 'N/A',
      utility_type: account.commodity_type || 'Unknown',
      supplier_name: account.vendor_name || 'Unknown',
      property_name: account.property_name,
      collection_configured: config?.collection_completed || false,
    };
  });

  const [activeTab, setActiveTab] = useState<'bill-sources' | 'gl-codes' | 'tags'>('bill-sources');
  const [billSources, setBillSources] = useState<BillSource[]>([]);
  const [glMappings, setGLMappings] = useState<GLCodeMapping[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>([]);

  // Bill Source Dialog State
  const [billSourceDialogOpen, setBillSourceDialogOpen] = useState(false);
  const [billSourceForm, setBillSourceForm] = useState<Partial<CreateBillSourceRequest>>({
    source_type: 'email',
    account_ids: [],
  });

  // GL Code Dialog State
  const [glDialogOpen, setGLDialogOpen] = useState(false);
  const [glForm, setGLForm] = useState<Partial<CreateGLCodeMappingRequest & { is_active?: boolean }>>({
    mapping_name: '',
    gl_account_code: '',
    is_active: true,
  });

  // Tag Dialog State
  const [tagDialogOpen, setTagDialogOpen] = useState(false);
  const [tagForm, setTagForm] = useState<Partial<Tag>>({
    tag_name: '',
    tag_color: '#3B82F6',
    tag_category: '',
  });

  useEffect(() => {
    if (project && currentWorkspace) {
      loadData();
    }
  }, [project, currentWorkspace, activeTab]);

  const loadData = async () => {
    if (!currentWorkspace) return;

    try {
      setLoading(true);
      switch (activeTab) {
        case 'bill-sources':
          const sources = await onboardingApi.listBillSources(currentWorkspace.id);
          setBillSources(sources);
          break;
        case 'gl-codes':
          const mappings = await onboardingApi.listGLMappings(currentWorkspace.id);
          setGLMappings(mappings);
          break;
        case 'tags':
          await loadTags();
          break;
      }
    } catch (error: any) {
      console.error('Failed to load data:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to load data',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const loadTags = async () => {
    if (!currentWorkspace) return;
    try {
      const loadedTags = await onboardingApi.listTags(currentWorkspace.id);
      setTags(loadedTags);
    } catch (error: any) {
      console.error('Failed to load tags:', error);
      // Fallback to empty array on error
      setTags([]);
    }
  };

  const handleCreateBillSource = async () => {
    if (!currentWorkspace || !billSourceForm.account_ids?.length) {
      toast({
        title: 'Validation Error',
        description: 'Please select at least one account',
        variant: 'destructive',
      });
      return;
    }

    try {
      const request: CreateBillSourceRequest = {
        source_type: billSourceForm.source_type || 'email',
        account_ids: billSourceForm.account_ids,
        config: {
          email_address: billSourceForm.config?.email_address,
          api_endpoint: billSourceForm.config?.api_endpoint,
          portal_url: billSourceForm.config?.portal_url,
          check_frequency: billSourceForm.config?.check_frequency || 'daily',
        },
      };

      const created = await onboardingApi.createBillSources(currentWorkspace.id, request);
      setBillSources([...billSources, ...created]);
      setBillSourceDialogOpen(false);
      setBillSourceForm({ source_type: 'email', account_ids: [] });
      toast({
        title: 'Success',
        description: `Created ${created.length} bill source(s)`,
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create bill source',
        variant: 'destructive',
      });
    }
  };

  const handleCreateGLMapping = async () => {
    if (!currentWorkspace || !glForm.mapping_name || !glForm.gl_account_code) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields',
        variant: 'destructive',
      });
      return;
    }

    try {
      const request: CreateGLCodeMappingRequest = {
        mapping_name: glForm.mapping_name!,
        gl_account_code: glForm.gl_account_code!,
        gl_cost_center: glForm.gl_cost_center,
        gl_department: glForm.gl_department,
        gl_project_code: glForm.gl_project_code,
        applies_to_utility_type: glForm.applies_to_utility_type,
        applies_to_tags: glForm.applies_to_tags || [],
        description: glForm.description,
        notes: glForm.notes,
      };

      const created = await onboardingApi.createGLMapping(currentWorkspace.id, request);
      setGLMappings([...glMappings, created]);
      setGLDialogOpen(false);
      setGLForm({ mapping_name: '', gl_account_code: '', is_active: true });
      toast({
        title: 'Success',
        description: 'GL code mapping created',
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create GL mapping',
        variant: 'destructive',
      });
    }
  };

  const handleCreateTag = async () => {
    if (!currentWorkspace || !tagForm.tag_name) {
      toast({
        title: 'Validation Error',
        description: 'Please enter a tag name',
        variant: 'destructive',
      });
      return;
    }

    try {
      const newTag = await onboardingApi.createTag(currentWorkspace.id, {
        tag_name: tagForm.tag_name,
        tag_color: tagForm.tag_color,
        tag_category: tagForm.tag_category,
        description: tagForm.description,
      });
      setTags([...tags, newTag]);
      setTagDialogOpen(false);
      setTagForm({ tag_name: '', tag_color: '#3B82F6', tag_category: '' });
      toast({
        title: 'Success',
        description: 'Tag created',
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create tag',
        variant: 'destructive',
      });
    }
  };

  const getSourceTypeIcon = (type: BillSourceType) => {
    switch (type) {
      case 'email':
        return <Mail className="h-4 w-4" />;
      case 'api':
        return <Globe className="h-4 w-4" />;
      case 'portal_scraping':
        return <FileText className="h-4 w-4" />;
      default:
        return <Settings className="h-4 w-4" />;
    }
  };

  const getSourceTypeLabel = (type: BillSourceType) => {
    switch (type) {
      case 'email':
        return 'Email';
      case 'api':
        return 'API';
      case 'portal_scraping':
        return 'Portal Scraping';
      case 'manual_upload':
        return 'Manual Upload';
      case 'ftp':
        return 'FTP';
      case 'partner_feed':
        return 'Partner Feed';
      default:
        return type;
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div>
        <h2 className="text-2xl font-semibold">Stage 4: Mapping & Configuration</h2>
        <p className="text-muted-foreground mt-1">
          Configure bill sources, GL code mappings, and tags for your accounts
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Bill Sources</p>
                <p className="text-2xl font-bold">{billSources.length}</p>
              </div>
              <Mail className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">GL Code Mappings</p>
                <p className="text-2xl font-bold">{glMappings.length}</p>
              </div>
              <FileText className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Tags</p>
                <p className="text-2xl font-bold">{tags.length}</p>
              </div>
              <TagIcon className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Configuration Tabs */}
      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as any)}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="bill-sources">Bill Sources</TabsTrigger>
          <TabsTrigger value="gl-codes">GL Code Mappings</TabsTrigger>
          <TabsTrigger value="tags">Tags</TabsTrigger>
        </TabsList>

        {/* Bill Sources Tab */}
        <TabsContent value="bill-sources" className="space-y-4 mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Bill Sources</CardTitle>
                  <CardDescription>
                    Configure how bills are received for each account
                  </CardDescription>
                </div>
                <Dialog open={billSourceDialogOpen} onOpenChange={setBillSourceDialogOpen}>
                  <DialogTrigger asChild>
                    <Button>
                      <Plus className="h-4 w-4 mr-2" />
                      Create Bill Source
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Create Bill Source</DialogTitle>
                      <DialogDescription>
                        Configure how bills will be received for selected accounts
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div>
                        <Label>Source Type</Label>
                        <Select
                          value={billSourceForm.source_type}
                          onValueChange={(value) =>
                            setBillSourceForm({ ...billSourceForm, source_type: value as BillSourceType })
                          }
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="email">Email</SelectItem>
                            <SelectItem value="api">API</SelectItem>
                            <SelectItem value="portal_scraping">Portal Scraping</SelectItem>
                            <SelectItem value="manual_upload">Manual Upload</SelectItem>
                            <SelectItem value="ftp">FTP</SelectItem>
                            <SelectItem value="partner_feed">Partner Feed</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label>Select Accounts</Label>
                        <div className="mt-2 space-y-2 max-h-48 overflow-y-auto border rounded-md p-2">
                          {loadingAccounts ? (
                            <div className="flex items-center justify-center py-4 text-muted-foreground">
                              <Loader2 className="h-4 w-4 animate-spin mr-2" />
                              Loading accounts...
                            </div>
                          ) : accounts.length === 0 ? (
                            <div className="text-center py-4 text-muted-foreground text-sm">
                              No accounts imported. Complete Stage 2 first.
                            </div>
                          ) : (
                            accounts.map((account) => (
                              <div
                                key={account.id}
                                className="flex items-center space-x-2 p-2 hover:bg-muted rounded"
                              >
                                <input
                                  type="checkbox"
                                  checked={billSourceForm.account_ids?.includes(account.id)}
                                  onChange={(e) => {
                                    const ids = billSourceForm.account_ids || [];
                                    if (e.target.checked) {
                                      setBillSourceForm({
                                        ...billSourceForm,
                                        account_ids: [...ids, account.id],
                                      });
                                    } else {
                                      setBillSourceForm({
                                        ...billSourceForm,
                                        account_ids: ids.filter((id) => id !== account.id),
                                      });
                                    }
                                  }}
                                />
                                <div className="flex-1">
                                  <p className="text-sm font-medium">{account.account_number}</p>
                                  <p className="text-xs text-muted-foreground">
                                    {account.utility_type} • {account.supplier_name}
                                  </p>
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </div>

                      {billSourceForm.source_type === 'email' && (
                        <div>
                          <Label>Email Address</Label>
                          <Input
                            value={billSourceForm.config?.email_address || ''}
                            onChange={(e) =>
                              setBillSourceForm({
                                ...billSourceForm,
                                config: {
                                  ...billSourceForm.config,
                                  email_address: e.target.value,
                                },
                              })
                            }
                            placeholder="bills@example.com"
                          />
                        </div>
                      )}

                      {billSourceForm.source_type === 'api' && (
                        <div>
                          <Label>API Endpoint</Label>
                          <Input
                            value={billSourceForm.config?.api_endpoint || ''}
                            onChange={(e) =>
                              setBillSourceForm({
                                ...billSourceForm,
                                config: {
                                  ...billSourceForm.config,
                                  api_endpoint: e.target.value,
                                },
                              })
                            }
                            placeholder="https://api.example.com/bills"
                          />
                        </div>
                      )}

                      {billSourceForm.source_type === 'portal_scraping' && (
                        <div>
                          <Label>Portal URL</Label>
                          <Input
                            value={billSourceForm.config?.portal_url || ''}
                            onChange={(e) =>
                              setBillSourceForm({
                                ...billSourceForm,
                                config: {
                                  ...billSourceForm.config,
                                  portal_url: e.target.value,
                                },
                              })
                            }
                            placeholder="https://portal.example.com"
                          />
                        </div>
                      )}

                      <div>
                        <Label>Check Frequency</Label>
                        <Select
                          value={billSourceForm.config?.check_frequency || 'daily'}
                          onValueChange={(value) =>
                            setBillSourceForm({
                              ...billSourceForm,
                              config: {
                                ...billSourceForm.config,
                                check_frequency: value as any,
                              },
                            })
                          }
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="real_time">Real-time</SelectItem>
                            <SelectItem value="hourly">Hourly</SelectItem>
                            <SelectItem value="daily">Daily</SelectItem>
                            <SelectItem value="weekly">Weekly</SelectItem>
                            <SelectItem value="manual">Manual</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setBillSourceDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button onClick={handleCreateBillSource}>Create</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {billSources.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Mail className="h-16 w-16 mx-auto mb-4 text-muted-foreground/50" />
                  <p className="text-lg font-medium">No Bill Sources</p>
                  <p className="text-sm">Create your first bill source to get started</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Source Type</TableHead>
                      <TableHead>Accounts</TableHead>
                      <TableHead>Configuration</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {billSources.map((source) => (
                      <TableRow key={source.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {getSourceTypeIcon(source.source_type)}
                            <span>{getSourceTypeLabel(source.source_type)}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{source.account_id}</Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {source.email_address || source.api_endpoint || source.portal_url || 'N/A'}
                        </TableCell>
                        <TableCell>
                          <Badge variant={source.is_active ? 'default' : 'secondary'}>
                            {source.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button variant="ghost" size="sm">
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* GL Code Mappings Tab */}
        <TabsContent value="gl-codes" className="space-y-4 mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>GL Code Mappings</CardTitle>
                  <CardDescription>
                    Map accounts to General Ledger codes for accounting integration
                  </CardDescription>
                </div>
                <Dialog open={glDialogOpen} onOpenChange={setGLDialogOpen}>
                  <DialogTrigger asChild>
                    <Button>
                      <Plus className="h-4 w-4 mr-2" />
                      Create Mapping
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Create GL Code Mapping</DialogTitle>
                      <DialogDescription>
                        Define rules for mapping accounts to GL codes
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div>
                        <Label>Mapping Name *</Label>
                        <Input
                          value={glForm.mapping_name || ''}
                          onChange={(e) =>
                            setGLForm({ ...glForm, mapping_name: e.target.value })
                          }
                          placeholder="Electric - Main Building"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>GL Account Code *</Label>
                          <Input
                            value={glForm.gl_account_code || ''}
                            onChange={(e) =>
                              setGLForm({ ...glForm, gl_account_code: e.target.value })
                            }
                            placeholder="6000"
                          />
                        </div>
                        <div>
                          <Label>Cost Center</Label>
                          <Input
                            value={glForm.gl_cost_center || ''}
                            onChange={(e) =>
                              setGLForm({ ...glForm, gl_cost_center: e.target.value })
                            }
                            placeholder="CC-001"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Department</Label>
                          <Input
                            value={glForm.gl_department || ''}
                            onChange={(e) =>
                              setGLForm({ ...glForm, gl_department: e.target.value })
                            }
                            placeholder="Operations"
                          />
                        </div>
                        <div>
                          <Label>Project Code</Label>
                          <Input
                            value={glForm.gl_project_code || ''}
                            onChange={(e) =>
                              setGLForm({ ...glForm, gl_project_code: e.target.value })
                            }
                            placeholder="PROJ-001"
                          />
                        </div>
                      </div>

                      <div>
                        <Label>Applies To Utility Type</Label>
                        <Select
                          value={glForm.applies_to_utility_type || 'all'}
                          onValueChange={(value) =>
                            setGLForm({ ...glForm, applies_to_utility_type: value === 'all' ? undefined : value })
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="All types" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Types</SelectItem>
                            <SelectItem value="Electric">Electric</SelectItem>
                            <SelectItem value="Gas">Gas</SelectItem>
                            <SelectItem value="Water">Water</SelectItem>
                            <SelectItem value="Sewer">Sewer</SelectItem>
                            <SelectItem value="Trash">Trash</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label>Description</Label>
                        <Textarea
                          value={glForm.description || ''}
                          onChange={(e) =>
                            setGLForm({ ...glForm, description: e.target.value })
                          }
                          placeholder="Description of this mapping rule"
                          rows={3}
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setGLDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button onClick={handleCreateGLMapping}>Create</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {glMappings.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <FileText className="h-16 w-16 mx-auto mb-4 text-muted-foreground/50" />
                  <p className="text-lg font-medium">No GL Code Mappings</p>
                  <p className="text-sm">Create mappings to integrate with your accounting system</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Mapping Name</TableHead>
                      <TableHead>GL Account Code</TableHead>
                      <TableHead>Applies To</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {glMappings.map((mapping) => (
                      <TableRow key={mapping.id}>
                        <TableCell className="font-medium">{mapping.mapping_name}</TableCell>
                        <TableCell>
                          <div className="font-mono text-sm">{mapping.gl_account_code}</div>
                          {mapping.gl_cost_center && (
                            <div className="text-xs text-muted-foreground">
                              CC: {mapping.gl_cost_center}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            {mapping.applies_to_utility_type && (
                              <Badge variant="outline">{mapping.applies_to_utility_type}</Badge>
                            )}
                            {mapping.applies_to_tags && mapping.applies_to_tags.length > 0 && (
                              <div className="flex gap-1 flex-wrap">
                                {mapping.applies_to_tags.map((tag, idx) => (
                                  <Badge key={idx} variant="secondary" className="text-xs">
                                    {tag}
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={mapping.is_active ? 'default' : 'secondary'}>
                            {mapping.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button variant="ghost" size="sm">
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tags Tab */}
        <TabsContent value="tags" className="space-y-4 mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Tags</CardTitle>
                  <CardDescription>
                    Create and manage tags for categorizing properties, units, and accounts
                  </CardDescription>
                </div>
                <Dialog open={tagDialogOpen} onOpenChange={setTagDialogOpen}>
                  <DialogTrigger asChild>
                    <Button>
                      <Plus className="h-4 w-4 mr-2" />
                      Create Tag
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Create Tag</DialogTitle>
                      <DialogDescription>
                        Create a new tag for categorizing entities
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div>
                        <Label>Tag Name *</Label>
                        <Input
                          value={tagForm.tag_name || ''}
                          onChange={(e) =>
                            setTagForm({ ...tagForm, tag_name: e.target.value })
                          }
                          placeholder="Residential"
                        />
                      </div>

                      <div>
                        <Label>Category</Label>
                        <Input
                          value={tagForm.tag_category || ''}
                          onChange={(e) =>
                            setTagForm({ ...tagForm, tag_category: e.target.value })
                          }
                          placeholder="property_type, priority, billing, etc."
                        />
                      </div>

                      <div>
                        <Label>Color</Label>
                        <div className="flex gap-2">
                          <Input
                            type="color"
                            value={tagForm.tag_color || '#3B82F6'}
                            onChange={(e) =>
                              setTagForm({ ...tagForm, tag_color: e.target.value })
                            }
                            className="w-20 h-10"
                          />
                          <Input
                            value={tagForm.tag_color || '#3B82F6'}
                            onChange={(e) =>
                              setTagForm({ ...tagForm, tag_color: e.target.value })
                            }
                            placeholder="#3B82F6"
                          />
                        </div>
                      </div>

                      <div>
                        <Label>Description</Label>
                        <Textarea
                          value={tagForm.description || ''}
                          onChange={(e) =>
                            setTagForm({ ...tagForm, description: e.target.value })
                          }
                          placeholder="Optional description"
                          rows={3}
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setTagDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button onClick={handleCreateTag}>Create</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {tags.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <TagIcon className="h-16 w-16 mx-auto mb-4 text-muted-foreground/50" />
                  <p className="text-lg font-medium">No Tags</p>
                  <p className="text-sm">Create tags to organize and categorize your data</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {tags.map((tag) => (
                    <Card key={tag.id} className="relative">
                      <CardContent className="pt-6">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <div
                              className="h-8 w-8 rounded-full"
                              style={{ backgroundColor: tag.tag_color }}
                            />
                            <div>
                              <p className="font-medium">{tag.tag_name}</p>
                              {tag.tag_category && (
                                <p className="text-xs text-muted-foreground">{tag.tag_category}</p>
                              )}
                              {tag.description && (
                                <p className="text-sm text-muted-foreground mt-1">
                                  {tag.description}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <Button variant="ghost" size="sm">
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Navigation */}
      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={() => navigate(`/w/${currentWorkspace?.id}/dashboard/tum/onboarding/project/${projectId}/stage-3`)}
        >
          ← Previous Stage
        </Button>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => {
              // Export bill sources, GL codes, and tags as CSV
              const csvRows = [
                'type,name,code/email,status',
                ...billSources.map(bs => `bill_source,${bs.source_type},${bs.email_address || bs.portal_url || bs.api_endpoint || ''},${bs.is_active ? 'active' : 'inactive'}`),
                ...glMappings.map(gl => `gl_code,${gl.mapping_name},${gl.gl_account_code},${gl.is_active ? 'active' : 'inactive'}`),
                ...tags.map(t => `tag,${t.tag_name},${t.tag_color},active`),
              ];
              const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `mapping-config-${project?.customer_name || 'export'}.csv`;
              a.click();
              URL.revokeObjectURL(url);
              toast({ title: 'Export complete', description: 'Mapping configuration exported.' });
            }}
          >
            <Download className="h-4 w-4 mr-2" />
            Export Config
          </Button>
          <Button
            onClick={() => {
              if (project) {
                onboardingApi.updateProject(project.id, {
                  current_stage: 'review',
                });
                navigate(`/w/${currentWorkspace?.id}/dashboard/tum/onboarding/project/${projectId}/stage-5`);
              }
            }}
          >
            Continue to Review →
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Stage4Mapping;

